import React, { useCallback } from 'react';

import TeamProfileCardTrigger from '@atlaskit/profilecard/team';
import { usePrincipalUser, useSite, useV3OrgIdStatus } from '@atlassian/ptc-common';

import { useProfileClient } from '../../state/ProfileClient';
import { prefixWithContextEntryPoint } from '../../utils/url';
import { useAppConfig, useFlags } from '../AppConfigContext';

import { type WithTeamProfilecardProps } from './types';

export default function WithTeamProfilecard(props: WithTeamProfilecardProps) {
	const {
		children,
		onClick,
		position = 'bottom-start',
		trigger = 'hover',
		triggerLinkType = 'link',
		teamId,
	} = props;

	const [{ cloudId }] = useSite();
	const addFlag = useFlags();

	const { contextEntryPoint, push } = useAppConfig();

	const profileClient = useProfileClient();
	const [{ principalUserId }] = usePrincipalUser();

	const { orgId, isOrgLoading } = useV3OrgIdStatus();

	const navigateToTeam = useCallback(() => push(`team/${teamId}`), [push, teamId]);

	const generateUserLink = useCallback(
		(userId: string) => prefixWithContextEntryPoint(userId, contextEntryPoint),
		[contextEntryPoint],
	);

	const onUserClick = useCallback((userId: string) => push(userId), [push]);

	if (isOrgLoading) {
		return children;
	}

	return (
		<TeamProfileCardTrigger
			position={position}
			orgId={orgId}
			teamId={teamId}
			resourceClient={profileClient}
			viewingUserId={principalUserId}
			trigger={trigger}
			triggerLinkType={triggerLinkType}
			viewProfileLink={prefixWithContextEntryPoint(`team/${teamId}`, contextEntryPoint)}
			viewProfileOnClick={onClick || navigateToTeam}
			generateUserLink={generateUserLink}
			onUserClick={onUserClick}
			addFlag={addFlag}
			cloudId={cloudId}
		>
			{children}
		</TeamProfileCardTrigger>
	);
}
